import { useState } from 'react';
import { Link } from 'react-router';
import { InfoHint } from '@/components/info-hint';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTheme } from '@/app/theme-provider';
import { useSession } from '@/app/session';
import type { ConsoleLocale } from '@/lib/api';
import { translate, useT } from '@/lib/i18n';
import { notify } from '@/lib/notify';
import { presetName } from '@/lib/themes';

/**
 * Profile — the signed-in user's own account.
 *
 * Name, email and role are read-only here **by design, not by omission**: the
 * role is assigned by an administrator through Settings → Console users and
 * enforced server-side, so an editable field would be offering something the
 * API refuses. (This screen previously carried a "Phase 4" badge and said the
 * fields would become editable when the auth scaffold landed. Phase 4 landed;
 * the fields are read-only for a reason that has nothing to do with phases.)
 *
 * **Language is the one setting that lives here** (Workstream H1), and its
 * placement is the decision: it is an ACCOUNT property, so it follows an
 * operator to whatever machine they sign in from. The theme is deliberately the
 * opposite — a device preference kept in `localStorage` and configured in
 * Settings → Appearance — which is why this tab links there rather than
 * offering a second picker that would drift from the first.
 */
export function Profile() {
  const { preset, mode } = useTheme();
  const { user, locale, setLocale } = useSession();
  const t = useT();
  const [saving, setSaving] = useState(false);

  async function handleLocaleChange(next: string) {
    setSaving(true);
    try {
      await setLocale(next as ConsoleLocale);
      /**
       * `translate(key, next)`, NOT `t(key)`.
       *
       * `t` came from `useT()` during the render that produced this handler, so
       * it is closed over the language being *left*. Awaiting the save does not
       * change that — the closure is fixed, and React has not re-rendered this
       * function. So the confirmation for switching to Arabic arrived in
       * English, which is precisely the one message that should not.
       *
       * Naming the target locale explicitly is also the honest expression of
       * what this line means: confirm in the language that now applies.
       */
      notify.success(translate('profile.language.saved', next));
    } catch {
      // The failure stays in the language still in force, because nothing changed.
      notify.error(t('profile.language.failed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title={t('profile.title')}
        description={t('profile.description')}
      />

      <Tabs defaultValue="account">
        <TabsList>
          <TabsTrigger value="account">{t('profile.tab.account')}</TabsTrigger>
          <TabsTrigger value="appearance">{t('profile.tab.appearance')}</TabsTrigger>
        </TabsList>

        <TabsContent value="account" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('profile.account.title')}</CardTitle>
              <CardDescription>{t('profile.account.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="name">{t('profile.name')}</Label>
                <Input
                  id="name"
                  value={user?.name ?? ''}
                  placeholder={t('profile.notSignedIn')}
                  disabled
                  readOnly
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">{t('login.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={user?.email ?? ''}
                  placeholder={t('profile.notSignedIn')}
                  disabled
                  readOnly
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="role">
                  {t('profile.role')}
                  <InfoHint>{t('profile.role.detail')}</InfoHint>
                </Label>
                <Input
                  id="role"
                  value={user ? t(user.role === 'admin' ? 'role.admin' : 'role.analyst') : ''}
                  placeholder={t('common.notAvailable')}
                  disabled
                  readOnly
                />
              </div>

              <Separator />

              {/* THE H1 CONTROL. */}
              <div className="grid gap-2">
                <Label htmlFor="locale">
                  {t('profile.language')}
                  <InfoHint>{t('profile.language.detail')}</InfoHint>
                </Label>
                <Select value={locale} onValueChange={handleLocaleChange} disabled={saving}>
                  <SelectTrigger id="locale" className="w-full sm:w-72">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Each language is named IN ITSELF, never translated. An
                        operator looking for Arabic is looking for "العربية";
                        rendering it as "Arabic" to an Arabic reader — or worse,
                        as "الإنجليزية" to an English one who cannot read it —
                        makes the one control that fixes a wrong language the
                        hardest one to find. */}
                    <SelectItem value="en-AE">English</SelectItem>
                    <SelectItem value="ar-AE">العربية</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="appearance" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('profile.theme.title')}</CardTitle>
              <CardDescription>{t('profile.theme.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <dl className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border p-4">
                  <dt className="text-muted-foreground text-xs">{t('profile.theme.preset')}</dt>
                  <dd className="mt-0.5 text-sm font-medium">{presetName(preset)}</dd>
                </div>
                <div className="rounded-lg border p-4">
                  <dt className="text-muted-foreground text-xs">{t('profile.theme.mode')}</dt>
                  <dd className="mt-0.5 text-sm font-medium">
                    {t(mode === 'dark' ? 'profile.theme.dark'
                      : mode === 'light' ? 'profile.theme.light'
                      : 'profile.theme.system')}
                  </dd>
                </div>
              </dl>
              <Button variant="outline" asChild>
                <Link to="/settings">{t('profile.theme.changeInSettings')}</Link>
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
